import { useState } from "react";
import { Grid2, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import { useMutation } from "@tanstack/react-query";
import { useTranslate } from "../../../hooks/useTranslate";
import StatusBox from "../../../components/StatusBox";
import SummaryBox from "../../../components/SummaryBox";
import ContactInformationForm from "./components/ContactInformationForm";
import BaseDialog from "../../../components/BaseDialog";
import ContactShortInfo from "./components/ContactShortInfo";
import { useGetStudentContacts } from "../../../hooks/useGetStudentContacts";
import { useStudents } from "../../../context/students";
import Preloader from "../../../components/Preloader";
import { useGetRelationTypes } from "../../../hooks/useGetRelationTypes";
import {
    EMERGENCY_TYPE, SIMPLE_TYPE, getContactTitleKey, isContactPrimary,
} from "./constants";
import axiosManager from "../../../services/axiosManager";
import SnackbarToast from "../../../components/SnackbarToast";
import { capitalizeFirstLetter } from "../../../utils/capitalizeFirstLetter";
import useStatuses from "../../../context/status/useStatuses";
import VerificationStatusChip from "../../../components/VerificationStatusChip";
import AddContactTypeModal from "../../../components/AddContactTypeModal";
import { CONTACT_METHOD } from "../../../constants/contactMethod";

const ResponsibleAdults = () => {
    const { t } = useTranslate();

    const { activeStudentId } = useStudents();
    const { relationshipsOptions, isLoading: isLoadingRelations } = useGetRelationTypes();
    const {
        contacts, isLoading: isLoadingContacts,
        refetch, isRefetching,
    } = useGetStudentContacts(activeStudentId);
    const {
        statuses, isLockedStatus, refetchPrimaryContactVerificationStatus, refetchContact,
    } = useStatuses();
    const isLocked = isLockedStatus.responsibleAdults;

    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [newContactType, setNewContactType] = useState(null);
    const [editContactId, setEditContactId] = useState(null);

    const onAddContact = (contactType = SIMPLE_TYPE) => () => {
        setAddDialogOpen(false);
        setNewContactType(contactType);
    };

    const {
        mutate: createContact, reset: resetCreateContact,
        isPending: isCreatingContact, isSuccess: isSuccessCreateContact, error: errorCreateContact,
    } = useMutation({
        mutationFn: (body) => axiosManager.post$(`/parent/api/contacts?method=${CONTACT_METHOD.ENROLMENT}`, body),
        onSuccess: () => {
            setNewContactType(null);
            refetch();
        },
    });

    const onCreateContact = (formData) => {
        const { country, ...formDataObj } = formData;
        const bodyData = Object.keys(formDataObj).reduce((acc, key) => {
            acc[capitalizeFirstLetter(key)] = formDataObj[key];
            return acc;
        }, {});

        createContact({
            ...bodyData,
            CountryOfResidence: country,
            ContactType: newContactType || SIMPLE_TYPE,
        });
    };

    const {
        mutateAsync: updateContact, reset: resetUpdateContact,
        isPending: isUpdatingContact, isSuccess: isSuccessUpdateContact, error: errorUpdateContact,
    } = useMutation({
        mutationFn: (body) => axiosManager.put$(`/parent/api/contacts/${editContactId}`, body),
    });
    const {
        mutateAsync: uploadProofOfId, reset: resetUploadProofOfId,
        isPending: isUploadingProofOfId, isSuccess: isSuccessUploadProofOfId, error: errorUploadProofOfId,
    } = useMutation({
        mutationFn: (body) => axiosManager.post$(`/parent/api/contacts/${editContactId}/ProofOfId`, body, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    });

    const onUpdateContact = async (formData) => {
        try {
            const {
                isDirtyForm, country, proofOfId, ...formDataObj
            } = formData;
            const bodyData = Object.keys(formDataObj).reduce((acc, key) => {
                acc[capitalizeFirstLetter(key)] = formDataObj[key];
                return acc;
            }, {});
            const promises = [];

            if (proofOfId) {
                const formDataBody = new FormData();
                formDataBody.append("file", proofOfId);
                promises.push(uploadProofOfId(formDataBody));
            }

            if (isDirtyForm) {
                promises.push(updateContact({
                    ...bodyData,
                    ContactId: editContactId,
                    CountryOfResidence: country,
                }));
            }

            await Promise.all(promises);

            setEditContactId(null);
            refetch();
            refetchPrimaryContactVerificationStatus();
            refetchContact();
        } catch (err) {
            console.error(err);
        }
    };

    const {
        mutate: deleteContact, reset: resetDeleteContact,
        isPending: isDeletingContact, isSuccess: isSuccessDeleteContact, error: errorDeleteContact,
    } = useMutation({
        mutationFn: (contactId) => axiosManager.delete$(`/parent/api/students/${activeStudentId}/contacts/${contactId}`),
        onSuccess: () => {
            refetch();
        },
    });

    const onDeleteContact = (contactId) => {
        deleteContact(contactId);
    };

    return (
        <div className="page-content">
            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, md: 7, lg: 8 }}>
                    <Typography variant="h1">
                        {t("responsibleAdults.title")}
                    </Typography>
                    <Typography variant="h3" className="mt-4" component="div">
                        {t("responsibleAdults.subtitle")}
                    </Typography>
                    <Typography variant="body1" className="mt-4" component="div">
                        {t("responsibleAdults.description")}
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 5, lg: 4 }} className="mb-8">
                    <StatusBox
                        title={t("responsibleAdults.responsibleAdultStatus")}
                        isSuccess={statuses.responsibleAdultsDetails.isSuccess && statuses.responsibleAdults.isSuccess}
                        isWarning={statuses.responsibleAdultsDetails.isWarning || statuses.responsibleAdults.isWarning}
                        isInfo={statuses.responsibleAdultsDetails.isInfo || statuses.responsibleAdults.isInfo}
                    >
                        <Typography variant="body2">
                            {t("responsibleAdults.responsibleAdultStatusDescription")}
                        </Typography>
                        <VerificationStatusChip
                            status={statuses.responsibleAdultsDetails.isSuccess ? statuses.responsibleAdults : statuses.responsibleAdultsDetails}
                        />
                    </StatusBox>
                </Grid2>
            </Grid2>
            <Preloader isLoading={isLoadingContacts}>
                <Preloader isLoading={isRefetching} overflow showInitiallyOverflow>
                    {contacts.map((contact) => (
                        <SummaryBox
                            key={contact.contactId}
                            title={t(getContactTitleKey(contact.contactType))}
                            className="mb-8"
                            contentClassName="flex flex-column gap-12"
                            isFormBox
                        >
                            <ContactShortInfo
                                contact={contact}
                                relationships={relationshipsOptions}
                                isActionsDisabled={!!newContactType || !!editContactId}
                                isDeletingContact={isDeletingContact}
                                isPrimaryContact={isContactPrimary(contact.contactType)}
                                onEditClick={() => setEditContactId(contact.contactId)}
                                onDelete={() => onDeleteContact(contact.contactId)}
                            />
                            {contact.contactId === editContactId && (
                                <ContactInformationForm
                                    data={contact}
                                    relationshipsOptions={relationshipsOptions}
                                    isDisabled={isLocked}
                                    isLoadingRelations={isLoadingRelations}
                                    isPrimaryContact={isContactPrimary(contact.contactType)}
                                    isSaving={isUpdatingContact || isUploadingProofOfId}
                                    onSave={onUpdateContact}
                                    onCancel={() => setEditContactId(null)}
                                />
                            )}
                        </SummaryBox>
                    ))}
                    {newContactType && (
                        <SummaryBox
                            title={t(getContactTitleKey(newContactType))}
                            className="mb-12"
                            isFormBox
                        >
                            <ContactInformationForm
                                relationshipsOptions={relationshipsOptions}
                                isLoadingRelations={isLoadingRelations}
                                isSaving={isCreatingContact}
                                onSave={onCreateContact}
                                onCancel={() => setNewContactType(null)}
                            />
                        </SummaryBox>
                    )}
                    {(!isLocked && !newContactType) && (
                        <SummaryBox
                            className="mb-12"
                            contentClassName="flex flex-column align-center"
                            isButton
                            onClick={() => setAddDialogOpen(true)}
                            disabled={!!editContactId}
                        >
                            <Typography variant="h4" className="text-primary" component="div">{t("responsibleAdults.addResponsibleAdult")}</Typography>
                            <AddCircleOutlineIcon className="text-primary text-4xl" />
                        </SummaryBox>
                    )}
                </Preloader>
            </Preloader>
            <AddContactTypeModal
                addDialogOpen={addDialogOpen}
                setAddDialogOpen={setAddDialogOpen}
                onAddContact={onAddContact}
            />
            <SnackbarToast
                open={isSuccessCreateContact || !!errorCreateContact}
                isSuccess={isSuccessCreateContact}
                isError={errorCreateContact}
                onClose={resetCreateContact}
            >
                {isSuccessCreateContact
                    ? t("responsibleAdults.contactInformation.successCreateMessage")
                    : errorCreateContact?.response?.status === 400 && errorCreateContact?.response?.data === "Primary contact duplicate detected" ? t("myDetails.errorPrimaryContactDuplicate") : `${t("responsibleAdults.contactInformation.errorCreateMessage")} (status ${errorCreateContact?.response?.status})`}
            </SnackbarToast>
            <SnackbarToast
                open={isSuccessUpdateContact || !!errorUpdateContact}
                isSuccess={isSuccessUpdateContact}
                isError={errorUpdateContact}
                onClose={resetUpdateContact}
            >
                {isSuccessUpdateContact
                    ? t("responsibleAdults.contactInformation.successUpdateMessage")
                    : `${t("responsibleAdults.contactInformation.errorUpdateMessage")} (status ${errorUpdateContact?.response?.status})`}
            </SnackbarToast>
            <SnackbarToast
                open={isSuccessDeleteContact || !!errorDeleteContact}
                isSuccess={isSuccessDeleteContact}
                isError={errorDeleteContact}
                onClose={resetDeleteContact}
            >
                {isSuccessDeleteContact
                    ? t("responsibleAdults.contactInformation.successDeleteMessage")
                    : `${t("responsibleAdults.contactInformation.errorDeleteMessage")} (status ${errorDeleteContact?.response?.status})`}
            </SnackbarToast>
            <SnackbarToast
                open={isSuccessUploadProofOfId || !!errorUploadProofOfId}
                isSuccess={isSuccessUploadProofOfId}
                isError={errorUploadProofOfId}
                onClose={resetUploadProofOfId}
            >
                {isSuccessUploadProofOfId
                    ? t("responsibleAdults.contactInformation.successUploadProofOfIdMessage")
                    : `${t("responsibleAdults.contactInformation.errorUploadProofOfIdMessage")} (status ${errorUploadProofOfId?.response?.status})`}
            </SnackbarToast>
        </div>
    );
};

export default ResponsibleAdults;
